import { inject, Injectable } from '@angular/core';
import { AssistResourceBM } from '@cue/api';
import { map, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../models/index';
import { allAreas } from '../selectors/index';
import { TranslocoService } from '@jsverse/transloco';
import { localized } from '@cue/translate';
import { toSignal } from '@angular/core/rxjs-interop';
import { Area, Pack, ResourceDetail } from '@cue/data';

export interface BreadcrumbItem {
  area: Area;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  store = inject(Store<AppState>);
  translocoService = inject(TranslocoService);
  #allAreas = toSignal<Pack<Area[]>>(this.store.select(allAreas()) as any);

  getAreaSync(areaId: number): Area | undefined {
    return this.#allAreas().data!.find((x) => x.id == areaId);
  }

  getAreaSyncByImageUrl(imageUrl: string): Area | undefined {
    return this.#allAreas().data!.find((x) => x.newFloorplan == imageUrl);
  }

  getNewMapUrlForResourceSync(resource: AssistResourceBM | ResourceDetail): string {
    const pointer = this.#allAreas().data!.find((x) => x.id == resource.mapInfos[0].areaId);
    return pointer.newFloorplan;
  }

  getBreadcrumbForResourceSync(resource: AssistResourceBM | ResourceDetail): BreadcrumbItem[] {
    let breadcrumb: BreadcrumbItem[] = [];

    let pointer = this.#allAreas().data!.find((x) => x.id == resource.mapInfos[0].areaId);

    do {
      breadcrumb = [
        {
          area: pointer,
          name: localized(pointer.name, this.translocoService),
        },
        ...breadcrumb,
      ];
      pointer = this.#allAreas().data!.find((x) => x.id == pointer.parentAreaId);
    } while (pointer?.parentAreaId != null);

    return breadcrumb;
  }

  getBreadcrumbForResource(resource: AssistResourceBM | ResourceDetail): Observable<Pack<BreadcrumbItem[]>> {
    return this.store.select(allAreas()).pipe(
      map((value) => {
        if (value.loading) {
          return {
            loading: true,
            data: [],
          };
        }

        let breadcrumb: BreadcrumbItem[] = [];

        let pointer = value.data.find((x) => x.id == resource.mapInfos[0].areaId);

        do {
          breadcrumb = [
            {
              area: pointer,
              name: localized(pointer.name, this.translocoService),
            },
            ...breadcrumb,
          ];
          pointer = value.data.find((x) => x.id == pointer.parentAreaId);
        } while (pointer?.parentAreaId != null);

        return {
          loading: false,
          data: breadcrumb,
        };
      })
    );
  }

  getBreadcrumbForResourceInstant(resource: AssistResourceBM, areas: Area[]): BreadcrumbItem[] {
    let breadcrumb: BreadcrumbItem[] = [];

    let pointer = areas.find((x) => x.id == resource.mapInfos[0].areaId);

    do {
      breadcrumb = [
        {
          area: pointer,
          name: localized(pointer.name, this.translocoService),
        },
        ...breadcrumb,
      ];
      pointer = areas.find((x) => x.id == pointer.parentAreaId);
    } while (pointer?.parentAreaId != null);
    return breadcrumb;
  }

  getNewMapUrlForResource(resource: AssistResourceBM | ResourceDetail): Observable<Pack<string>> {
    return this.store.select(allAreas()).pipe(
      map((value) => {
        if (value.loading) {
          return {
            loading: true,
            data: '',
          };
        }

        const pointer = value.data.find((x) => x.id == resource.mapInfos[0].areaId);
        return {
          loading: false,
          data: pointer.newFloorplan,
        };
      })
    );
  }
}
